import { DocumentReader } from '../../word/api/DocumentReader';
import { RedlineInserter } from '../../word/trackChanges/RedlineInserter';
import { SelectionTracker } from '../../word/api/SelectionTracker';
import { AIOrchestrator } from '../../ai/orchestration/AIOrchestrator';
import { SelectionManager } from './SelectionManager';
import { RephraseOptions as ServiceRephraseOptions } from './types';
import { RephraseOptions as AIRephraseOptions, RephraseResult } from '../../ai/types/aiTypes';
import { SelectionInfo } from '../../word/types/wordTypes';
import { log, logError } from '../../utils/logging';
import { AppError, handleError } from '../../utils/errorHandling';
import { TrackChangesParser } from '@/word/utils/TrackChangesParser';
import { RobustXmlTrackChangesGenerator } from './RobustXmlTrackChangesGenerator';

export class RephrasingService {
  private aiOrchestrator: AIOrchestrator;
  private selectionManager: SelectionManager;
  private selectionTracker: SelectionTracker;
  private currentRephrasedResult: RephraseResult | null = null;
  private originalSelectedText: string | null = null; // Store original text for replacement

  constructor(aiOrchestrator: AIOrchestrator) {
    this.aiOrchestrator = aiOrchestrator;
    this.selectionManager = new SelectionManager();
    this.selectionTracker = new SelectionTracker();
    this.selectionTracker.onSelectionChange((selection) => {
      this.selectionManager.setSelection(selection);
    });
  }

  /** Start live selection tracking (Editor tab). No-op outside Word. */
  startSelectionTracking(): void {
    this.selectionTracker.startTracking();
  }

  /** Stop live selection tracking when Editor is not visible. */
  stopSelectionTracking(): void {
    this.selectionTracker.stopTracking();
  }

  /**
   * Gets the selection manager for UI to subscribe to selection changes
   */
  getSelectionManager(): SelectionManager {
    return this.selectionManager;
  }

  /**
   * Gets the current selection info
   */
  async getSelectionInfo(): Promise<SelectionInfo> {
    return await this.refreshSelection();
  }

  /**
   * Refreshes the current selection from the document
   * Uses getSelectionOrParagraph to support click-to-select paragraph
   */
  async refreshSelection(): Promise<SelectionInfo> {
    try {
      const selection = await DocumentReader.getSelectionOrParagraph();
      
      this.selectionManager.setSelection(selection);
      
      return selection;
    } catch (error) {
      logError('Failed to refresh selection', error);
      throw error;
    }
  }

  /**
   * Polishes the currently selected text (minor edits)
   */
  async polish(options?: ServiceRephraseOptions): Promise<RephraseResult> {
    return this.rephrase(options, false);
  }

  /**
   * Completely rephrases the currently selected text
   */
  async rephraseComplete(options?: ServiceRephraseOptions): Promise<RephraseResult> {
    return this.rephrase(options, true);
  }

  /**
   * Rephrases the currently selected text
   * @param completeRephrase - If true, performs a complete rephrase (not just polishing)
   */
  private async rephrase(options?: ServiceRephraseOptions, completeRephrase: boolean = false): Promise<RephraseResult> {
    try {
      // Get current selection
      const selection = await this.refreshSelection();
      
      if (!selection || selection.isEmpty) {
        throw new AppError(
          'No text selected',
          'NO_SELECTION',
          'Please select some text in the document.'
        );
      }

      // Log selection details for debugging
      log(`Selection details:`, {
        textLength: selection.text?.length || 0,
        hasOoxml: !!selection.ooxml,
        ooxmlLength: selection.ooxml?.length || 0,
        ooxmlPreview: selection.ooxml?.substring(0, 200) || 'N/A'
      });

      // Convert service options to AI options
      const aiOptions: AIRephraseOptions = {
        tone: options?.tone,
        length: options?.length,
      };

      // Store original text for later replacement
      this.originalSelectedText = selection.text;

      // If selection has OOXML, pass it to the AI provider
      if (selection.ooxml && selection.ooxml.length > 0) {
        log(`Passing paragraph XML to AI provider (length: ${selection.ooxml.length})`);
        // Set XML on the AI provider before calling rephrase
        const provider = this.aiOrchestrator.getCurrentProvider();
        if (provider && 'setParagraphXml' in provider && typeof (provider as any).setParagraphXml === 'function') {
          (provider as any).setParagraphXml(selection.ooxml);
          log(`XML set on provider successfully`);
        } else {
          logError('Provider does not support setParagraphXml method', {
            providerType: provider?.constructor?.name,
            hasMethod: 'setParagraphXml' in (provider || {})
          });
        }
      } else {
        logError('Selection does not have OOXML', {
          ooxml: selection.ooxml,
          ooxmlType: typeof selection.ooxml,
          ooxmlLength: selection.ooxml?.length
        });
      }

      // Call AI orchestrator
      log(`Starting ${completeRephrase ? 'complete rephrase' : 'polish'} operation`);
      const result = await this.aiOrchestrator.rephrase(selection.text, aiOptions, completeRephrase);
      
      // Store result
      this.currentRephrasedResult = result;
      
      log(`${completeRephrase ? 'Complete rephrase' : 'Polish'} operation completed`, {
        hasParagraphXml: !!result.paragraphXml,
        xmlLength: result.paragraphXml?.length || 0
      });
      return result;
    } catch (error) {
      logError(`${completeRephrase ? 'Complete rephrase' : 'Polish'} operation failed`, error);
      this.currentRephrasedResult = null;
      throw error;
    }
  }

  /**
   * Gets the current rephrased result
   */
  getRephrasedResult(): RephraseResult | null {
    return this.currentRephrasedResult;
  }

  /**
   * Gets the original selected text (before polishing)
   */
  getOriginalText(): string | null {
    return this.originalSelectedText;
  }

  /**
   * Applies the rephrased text to the document as a tracked change
   * Searches for the original text and replaces it with the rephrased version
   * The replacement will appear as a redline/tracked change
   */
  async apply(): Promise<void> {
    if (!this.currentRephrasedResult || !this.originalSelectedText) {
      throw new AppError(
        'No polished result to apply',
        'NO_RESULT',
        'Please polish some text first before applying.'
      );
    }

    try {
      log('[RepharsingService] Applying rephrased text to document as tracked change');
      var trackChangesHtml = undefined;
      var selection = await this.refreshSelection();
      if (this.currentRephrasedResult.paragraphXml){
        trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(this.currentRephrasedResult.paragraphXml);
      } else if (selection.ooxml) {
        const editedOoxml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            selection.ooxml,
            this.currentRephrasedResult.rephrasedText,
            'AI Suggestion'
          );
        trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(editedOoxml);
      } else {
        throw new Error('Could not find paragraph. Please select it and try again.');
      }

      // Use search to find and replace the original text

      await RedlineInserter.applyWordLevelChangesIncrementally(
        this.originalSelectedText,
        this.currentRephrasedResult.rephrasedText,
        trackChangesHtml,
        selection.paragraphIndex
      );

      // Clear the result after applying
      this.currentRephrasedResult = null;
      this.originalSelectedText = null;

      log('Rephrased text applied successfully as tracked change');
    } catch (error) {
      logError('Failed to apply rephrased text', error);
      throw new AppError(
        'Failed to apply rephrased text',
        'APPLY_FAILED',
        handleError(error)
      );
    }
  }

  /**
   * Clears the current rephrased result
   */
  clearResult(): void {
    this.currentRephrasedResult = null;
    this.originalSelectedText = null;
  }

  /**
   * Clears the current selection
   */
  clearSelection(): void {
    this.selectionManager.clearSelection();
  }

  /**
   * Sets an empty selection explicitly (useful after applying changes)
   */
  setEmptySelection(): void {
    const emptySelection: SelectionInfo = {
      text: '',
      isEmpty: true,
      range: undefined,
    };
    this.selectionManager.setSelection(emptySelection);
  }
}

